import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
  getBlockedForExport,
  getFilters,
  getSearchView,
} from 'selectors/search';
import { useDefaultCategories } from 'hooks/useDefaultSearchCategories';
import {
  CARRY_FILTERS_PARAM,
  hasCarryableFilters,
} from 'components/Organisation/carryoverFilters';

/**
 * Builds links to an organisation profile from a search result, pointing the
 * profile at the filters the user is already searching with.
 *
 * Edited-but-not-yet-searched filters are deliberately not carried. The results
 * on screen still belong to the last search that ran — which is what the "Press
 * Search to apply changes" warning is telling the user — so carrying the pending
 * bag would open a profile narrowed by filters the user has not seen applied
 * anywhere. `blockedForExport` is the flag that warning reads, and it means the
 * same thing here: the visible results and the filter bag have diverged.
 *
 * With nothing worth carrying the link is byte-for-byte what it is today, so a
 * result set with no filters behaves exactly as before. Only search results
 * should use this — every other route to a profile is unrelated to the current
 * search and must keep arriving clean.
 *
 * @returns {(superOrganisationId: number | string) => string}
 */
const useOrgProfileLink = () => {
  const filters = useSelector(getFilters);
  const searchView = useSelector(getSearchView);
  const resultsAreStale = useSelector(getBlockedForExport);
  const defaultCategories = useDefaultCategories();
  const carries =
    !resultsAreStale &&
    hasCarryableFilters(filters, searchView, defaultCategories);

  return useCallback(
    superOrganisationId =>
      carries
        ? `/organisation/${superOrganisationId}?${CARRY_FILTERS_PARAM}=true`
        : `/organisation/${superOrganisationId}`,
    [carries]
  );
};

export default useOrgProfileLink;
